import { useState, useEffect, useCallback, useRef } from "react";
import {
  Stack,
  useToast,
  Box,
  Flex,
  Text,
  Button,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Badge,
  MenuButton,
  Menu,
  MenuList,
  MenuItem,
  IconButton,
  Switch,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import { Loading } from "../../../components/Loading";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { FiMoreVertical, FiPlus } from "react-icons/fi";
import {
  createVendorOnboardingConfig,
  getVendorOnboardingConfigs,
  updateVendorOnboardingConfig,
  toggleVendorOnboardingConfigStatus,
  deleteVendorOnboardingConfig,
} from "../../../actions/vendorOnboardingConfig.action";

const VendorOnboardingConfig = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isOpenDelete, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure();
  const cancelRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [editingConfig, setEditingConfig] = useState(null);
  const [deletingConfigId, setDeletingConfigId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    location: "",
    autoApproval: false,
    maxVehicles: 50,
    isActive: true,
  });

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getVendorOnboardingConfigs();
      setConfigs(result.data || []);
    } catch (error) {
      toast({
        title: "Failed to fetch configurations!",
        description: error?.message || "Please try again",
        status: "error",
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const resetForm = () => {
    setFormData({
      location: "",
      autoApproval: false,
      maxVehicles: 50,
      isActive: true,
    });
    setEditingConfig(null);
  };

  const handleSave = async () => {
    if (!formData.location.trim()) {
      toast({
        title: "Location name is required",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    setSubmitting(true);
    try {
      const parsedMaxVehicles = Number.parseInt(String(formData.maxVehicles).trim(), 10);
      const maxVehicles = Number.isFinite(parsedMaxVehicles) && parsedMaxVehicles >= 0 ? parsedMaxVehicles : 100;

      const payload = {
        location: formData.location.trim(),
        autoApproval: formData.autoApproval,
        maxVehicles,
        isActive: formData.isActive,
      };

      if (editingConfig) {
        await updateVendorOnboardingConfig(editingConfig._id, payload);
        toast({
          title: "Configuration updated successfully!",
          status: "success",
          duration: 3000,
        });
      } else {
        await createVendorOnboardingConfig(payload);
        toast({
          title: "Configuration created successfully!",
          status: "success",
          duration: 3000,
        });
      }

      onClose();
      resetForm();
      await loadConfigs();
    } catch (error) {
      toast({
        title: "Failed to save configuration!",
        description: error?.message || "Please try again",
        status: "error",
        duration: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      location: config.location,
      autoApproval: config.autoApproval,
      maxVehicles: config.maxVehicles,
      isActive: config.isActive,
    });
    onOpen();
  };

  const handleToggleStatus = async (configId, currentStatus) => {
    try {
      await toggleVendorOnboardingConfigStatus(configId, !currentStatus);
      setConfigs(prev =>
        prev.map(c =>
          c._id === configId ? { ...c, isActive: !currentStatus } : c
        )
      );
      toast({
        title: `Configuration ${!currentStatus ? "activated" : "deactivated"}!`,
        status: "success",
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: "Failed to update status!",
        description: error?.message || "Please try again",
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await deleteVendorOnboardingConfig(deletingConfigId);
      setConfigs(prev => prev.filter(c => c._id !== deletingConfigId));
      toast({
        title: "Configuration deleted successfully!",
        status: "success",
        duration: 3000,
      });
      onCloseDelete();
      setDeletingConfigId(null);
    } catch (error) {
      toast({
        title: "Failed to delete configuration!",
        description: error?.message || "Please try again",
        status: "error",
        duration: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <Stack p={6} gap={4}>
      <BreadCrumbMaker
        items={[
          { link: "/verification", title: "Verification Center" },
          { link: "#", title: "Vendor Onboarding Config", on: true },
        ]}
      />

      <Box bg="white" borderRadius="xl" p={4} shadow="sm">
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontSize="xl" fontWeight="bold" color="gray.700">
            Vendor Onboarding Configuration
          </Text>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            onClick={() => {
              resetForm();
              onOpen();
            }}
          >
            Add Configuration
          </Button>
        </Flex>

        <Box overflowX="auto">
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Name</Th>
                <Th>Auto Approval</Th>
                <Th>Max Vehicles</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {configs.length === 0 ? (
                <Tr>
                  <Td colSpan={5} textAlign="center" py={8}>
                    <Text color="gray.500">No configurations found</Text>
                  </Td>
                </Tr>
              ) : (
                configs.map((config) => (
                  <Tr key={config._id} _hover={{ bg: "gray.50" }}>
                    <Td fontWeight="medium">{config.location}</Td>
                    <Td>
                      <Badge colorScheme={config.autoApproval ? "green" : "gray"}>
                        {config.autoApproval ? "Yes" : "No"}
                      </Badge>
                    </Td>
                    <Td>{config.maxVehicles}</Td>
                    <Td>
                      <Switch
                        isChecked={config.isActive}
                        onChange={() => handleToggleStatus(config._id, config.isActive)}
                        colorScheme="green"
                      />
                    </Td>
                    <Td>
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<FiMoreVertical />}
                          variant="ghost"
                          size="sm"
                        />
                        <MenuList>
                          <MenuItem onClick={() => handleEdit(config)}>Edit</MenuItem>
                          <MenuItem
                            color="red.500"
                            onClick={() => {
                              setDeletingConfigId(config._id);
                              onOpenDelete();
                            }}
                          >
                            Delete
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
      </Box>

      {/* Add/Edit Configuration Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingConfig ? "Edit Configuration" : "Add New Configuration"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Location</FormLabel>
                <Input
                  placeholder="e.g., Noida or Mumbai"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Maximum Vehicles</FormLabel>
                <Input
                  type="number"
                  placeholder="e.g., 50"
                  value={formData.maxVehicles}
                  onChange={(e) => setFormData({ ...formData, maxVehicles: e.target.value })}
                />
              </FormControl>


              <FormControl display="flex" alignItems="center">
                <FormLabel mb={0}>Active</FormLabel>
                <Switch
                  isChecked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  colorScheme="green"
                />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} isDisabled={submitting}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSave}
              isLoading={submitting}
            >
              {editingConfig ? "Update" : "Create"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog isOpen={isOpenDelete} leastDestructiveRef={cancelRef} onClose={onCloseDelete}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Configuration
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete this configuration? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseDelete} isDisabled={submitting}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDelete}
                ml={3}
                isLoading={submitting}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Stack>
  );
};

export default VendorOnboardingConfig;
